import { Plus, X } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
  currency,
  MARGIN_THRESHOLDS,
  marginTone,
  quoteLabel,
  totalQty,
  type UiValues,
} from './_shared';
import { addPinned, type PinnedScenario, removePinned } from './calc-pinned';

/**
 * Proposal C — scenario tabs row. Sits above the form and promotes
 * pin/compare from a buried action to the page's primary mental model.
 *
 *   [ Live ] [ Pin A ] [ Pin B ] [ + Pin current ]
 *
 * The "Live" tab is always first and reflects the form's current
 * state — clicking it is a no-op when already live. Pin tabs are
 * snapshots stored in localStorage via `calc-pinned.ts`; clicking one
 * loads it into the form (`form.reset`). "+ Pin current" snapshots
 * whatever the form holds right now.
 *
 * Each pin tab carries a health-tone dot (green/amber/red) tied to its
 * snapshot's margin so the whole row reads as a scenario overview at a
 * glance.
 *
 * When live margin dips below the poor threshold, the Live tab's dot
 * turns red; tabs become the sentinel instead of needing a separate
 * alert. Keeps the visual footprint tiny and honest.
 */
export function ScenarioTabs({
  form,
  liveValues,
  liveResult,
  pinned,
  activePinId,
  onActivePin,
  onChange,
}: {
  form: UseFormReturn<UiValues>;
  liveValues: UiValues;
  liveResult: { perUnitSell: number; marginPct: number; floorPerUnit: number } | null;
  pinned: PinnedScenario[];
  activePinId: string | null;
  onActivePin: (id: string | null) => void;
  onChange: (next: PinnedScenario[]) => void;
}) {
  const liveActive = activePinId === null;
  const liveLabel = quoteLabel(liveValues);
  const liveTone = liveResult ? marginTone(liveResult.marginPct) : 'neutral';

  function handleActivateLive() {
    onActivePin(null);
  }

  function handleActivatePin(pin: PinnedScenario) {
    form.reset(pin.values, { keepDefaultValues: true });
    onActivePin(pin.id);
  }

  function handleAddNew() {
    if (!liveResult) return;
    const next = addPinned({
      at: Date.now(),
      values: liveValues,
      summary: {
        label: liveLabel,
        perUnitSell: liveResult.perUnitSell,
        orderTotal: liveResult.perUnitSell * totalQty(liveValues),
        marginPct: liveResult.marginPct,
        totalQty: totalQty(liveValues),
        mode: liveValues.mode,
      },
    });
    onChange(next);
    // Switch to the newly pinned scenario so the pin becomes the
    // active surface — matches "click a tab, start editing it" muscle
    // memory from browser tabs.
    const added = next[next.length - 1];
    if (added) onActivePin(added.id);
  }

  function handleRemove(id: string) {
    const next = removePinned(id);
    onChange(next);
    if (id === activePinId) onActivePin(null);
  }

  // `addPinned` auto-evicts the oldest when at cap, so we don't gate
  // the button on pinned.length. Only block when there's nothing to
  // snapshot (no live quote yet).
  const canPin = liveResult !== null;

  return (
    <div className="flex items-center gap-1 overflow-x-auto rounded-lg border bg-card p-1 shadow-sm">
      <TabPill
        active={liveActive}
        onClick={handleActivateLive}
        dot={liveTone}
        breach={liveResult ? liveResult.perUnitSell < liveResult.floorPerUnit : false}
        label="Live"
        sublabel={
          liveResult
            ? `${currency(liveResult.perUnitSell)} · ${liveResult.marginPct.toFixed(0)}%`
            : liveLabel
        }
      />
      {pinned.map((p) => (
        <TabPill
          key={p.id}
          active={activePinId === p.id}
          onClick={() => handleActivatePin(p)}
          onRemove={() => handleRemove(p.id)}
          dot={toneForPinned(p)}
          breach={p.summary.marginPct < 0}
          label={p.summary.label}
          sublabel={`${currency(p.summary.perUnitSell)} · ${p.summary.marginPct.toFixed(0)}%`}
        />
      ))}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="ml-auto shrink-0 gap-1 text-xs text-muted-foreground"
        onClick={handleAddNew}
        disabled={!canPin}
        title={
          canPin
            ? 'Save current quote as a new scenario (auto-replaces oldest at cap)'
            : 'Waiting for a live quote before pinning'
        }
      >
        <Plus className="size-3.5" />
        New
      </Button>
    </div>
  );
}

function TabPill({
  active,
  onClick,
  onRemove,
  dot,
  breach,
  label,
  sublabel,
}: {
  active: boolean;
  onClick: () => void;
  onRemove?: () => void;
  dot: 'positive' | 'neutral' | 'negative';
  breach: boolean;
  label: string;
  sublabel: string;
}) {
  const dotClass =
    dot === 'positive' ? 'bg-emerald-500' : dot === 'negative' ? 'bg-destructive' : 'bg-amber-500';
  return (
    <div
      className={`group relative flex shrink-0 items-center gap-2 rounded-md px-3 py-1.5 transition-colors ${
        active ? 'bg-primary/10 ring-1 ring-primary/30' : 'hover:bg-muted/60'
      } ${breach ? 'ring-1 ring-destructive/60' : ''}`}
    >
      <button type="button" onClick={onClick} className="flex min-w-0 items-center gap-2 text-left">
        <span className={`size-2 shrink-0 rounded-full ${dotClass}`} aria-hidden />
        <span className="flex min-w-0 flex-col leading-tight">
          <span
            className={`truncate text-xs font-medium ${active ? 'text-foreground' : 'text-muted-foreground'}`}
          >
            {label}
          </span>
          <span className="truncate text-[10px] text-muted-foreground tabular-nums">
            {sublabel}
          </span>
        </span>
      </button>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="shrink-0 rounded-sm p-0.5 text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
          aria-label="Remove scenario"
        >
          <X className="size-3" />
        </button>
      )}
    </div>
  );
}

function toneForPinned(p: PinnedScenario): 'positive' | 'neutral' | 'negative' {
  const m = p.summary.marginPct;
  if (m >= MARGIN_THRESHOLDS.ok) return 'positive';
  if (m < MARGIN_THRESHOLDS.poor) return 'negative';
  return 'neutral';
}
